import React, { Suspense, useEffect, useMemo, useState } from "react";
import {
  AHButton,
  AHIcon,
  AHInput,
  AHSeparator,
  AHText,
  Box,
  Circle,
  ListItem,
  TouchableBox,
} from "arkh";
import { formatBytes } from "./formatBytes";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
  useSubscription,
} from "react-relay";
import { FileUploadAreaQuery } from "./__generated__/FileUploadAreaQuery.graphql";
import {
  FileType,
  FileUploadAreaMutation,
} from "./__generated__/FileUploadAreaMutation.graphql";
import * as FileSystem from "expo-file-system/legacy";
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
} from "react-native";
import { BlurView } from "expo-blur";

import { DropFileZone } from "./DropFileZone";
import { HeaderButton } from "../../components";

import { FileUploadAreaWorkspaceMutation } from "./__generated__/FileUploadAreaWorkspaceMutation.graphql";
import * as DocumentPicker from "expo-document-picker";
import cuid from "cuid";
import { FileUploadAreaSubscription } from "./__generated__/FileUploadAreaSubscription.graphql";
import {
  FileStatus,
  FileUploadAreaStatusQuery,
} from "./__generated__/FileUploadAreaStatusQuery.graphql";
import { FileUploadAreaWorkspace_workspace$key } from "./__generated__/FileUploadAreaWorkspace_workspace.graphql";

import { FileRowWrapper } from "../OrgHome/WorkspaceFiles/FileRowWrapper";
import { FormattedMessage } from "react-intl";
import { removeExtension } from "./removeExtension";
import { mimeToType } from "./fileUtils/mimeToType";
import { MAX_FILE_SIZE, SUPPORTED_FILE_TYPES } from "./fileUtils/fileConstants";
import { Header } from "@react-navigation/elements";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type ErrorReason = "size" | "type" | "tooMany" | "unknown";

//  Filutrymmet är fullt. Radera filer för att kunna ladda upp nya.
function FileQuoataExceded(props: { onDismiss: () => void }) {
  return (
    <Box
      style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
      spaceBottom="large"
    >
      <Box
        cornerRadius="large"
        space="xxlarge"
        style={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: 150,
          overflow: "hidden",
        }}
      >
        <AHText color="darkerText" type="title">
          <FormattedMessage
            defaultMessage="Too many files"
            description="File Upload Area: Too many files"
          />
        </AHText>
        <AHText color="darkerText" type="body">
          <FormattedMessage
            defaultMessage="The storage is full. Delete files to upload new ones."
            description="File Upload Area: Storage full"
          />
        </AHText>
      </Box>
      <AHButton
        style={{ minWidth: 200 }}
        title="OK"
        themeType="primary"
        onPress={props.onDismiss}
      />
    </Box>
  );
}

export type PendingFile = {
  id: string;
  file: File | string;
  name: string;
  type: string;
  size: number;
};
export type PendingFileFile = {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
};

type FileUploadAreaProps = {
  files: PendingFile[];
  onDropedFiles: (files: PendingFile[]) => void;
  onRemoveFile(id: string): void;
  onCancel(): void;
  onUploadedFiles?(fileIds: string[]): void;
};

export function getDocumentsFromPicker() {
  return DocumentPicker.getDocumentAsync({
    copyToCacheDirectory: true,
    multiple: true,

    type: SUPPORTED_FILE_TYPES,
  }).then((result) => {
    if (result.output) {
      const out: File[] = [];
      for (const f of result.output) {
        out.push(f);
      }
      const newFiles = out
        .filter((file) => !file.size || file.size <= MAX_FILE_SIZE)
        .map((file) => ({
          file,
          type: file.type,
          name: removeExtension(file.name),
          id: cuid(),
          size: file.size,
        }));

      return {
        newFiles,
        hasError: result.output.length !== newFiles.length,
      };
    } else if (result.assets) {
      const newFiles = result.assets
        .filter((file) => !file.size || file.size <= MAX_FILE_SIZE)
        .map((file) => ({
          file: file.uri,
          type: file.mimeType!,
          name: removeExtension(file.name),
          id: cuid(),
          size: file.size!,
        }));

      return {
        newFiles,
        hasError: result.assets.length !== newFiles.length,
      };
    }
  });
}

export function WorkspaceFileUploadArea(
  props: FileUploadAreaProps & {
    fetchKey: string;
    workspace: FileUploadAreaWorkspace_workspace$key;
  }
) {
  const workspace = useFragment(
    graphql`
      fragment FileUploadAreaWorkspace_workspace on Workspace {
        id
      }
    `,
    props.workspace
  );
  const [getUploadLink] = useMutation<FileUploadAreaWorkspaceMutation>(graphql`
    mutation FileUploadAreaWorkspaceMutation(
      $name: String
      $type: FileType!
      $workspaceId: ID!
    ) {
      workspace(id: $workspaceId) {
        generateFileUploadLink(type: $type, name: $name) {
          id
          url
        }
      }
    }
  `);

  return (
    <InnerFileUploadArea
      {...props}
      getUploadLink={(name, type) => {
        return new Promise((resolve, reject) => {
          getUploadLink({
            variables: { name, type, workspaceId: workspace.id },
            onError: (e) => {
              reject(e);
            },
            onCompleted: (res) => {
              resolve({
                id: res.workspace.generateFileUploadLink.id,
                url: res.workspace.generateFileUploadLink.url,
              });
            },
          });
        });
      }}
    />
  );
}

export function StoreFileUploadArea(
  props: FileUploadAreaProps & {
    fetchKey: string;
  }
) {
  const quoataQuery = useLazyLoadQuery<FileUploadAreaQuery>(
    graphql`
      query FileUploadAreaQuery {
        store {
          id
          filesQuota {
            quota
            used
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [getUploadLink] = useMutation<FileUploadAreaMutation>(graphql`
    mutation FileUploadAreaMutation($name: String, $type: FileType!) {
      store {
        generateUploadLink(type: $type, name: $name) {
          id
          url
        }
      }
    }
  `);

  return (
    <InnerFileUploadArea
      {...props}
      quoata={quoataQuery.store.filesQuota}
      getUploadLink={(name, type) => {
        return new Promise((resolve, reject) => {
          getUploadLink({
            variables: { name, type },
            onError: (e) => {
              reject(e);
            },
            onCompleted: (res) => {
              resolve({
                id: res.store.generateUploadLink.id,
                url: res.store.generateUploadLink.url,
              });
            },
          });
        });
      }}
    />
  );
}

type CanonicalStatus = "none" | "error" | "uploading" | "done" | "processing";
function downlevelStatus(
  status: "uploaded" | "error" | FileStatus | undefined
): CanonicalStatus {
  switch (status) {
    case "uploaded":
      return "uploading";
    case "WAIT_FOR_PROCESSING":
    case "PROCESSING":
    case "READY":
      return "done";
    case "ERROR":
    case "error":
      return "error" as const;
    case "PENDING":
      return "processing";
  }
  return "none";
}

const StatusMessage = (props: { status: CanonicalStatus }) => {
  switch (props.status) {
    case "done":
      return (
        <FormattedMessage
          defaultMessage="Uploaded"
          description="File Upload Status Upload Complete"
        />
      );
    case "error":
      return (
        <FormattedMessage
          defaultMessage="Error"
          description="File Upload Status Upload Error"
        />
      );
    case "none":
      return (
        <FormattedMessage
          defaultMessage="Ready for Upload"
          description="File Upload Status Upload Pending"
        />
      );
    case "uploading":
      return (
        <FormattedMessage
          defaultMessage="Uploading"
          description="File Upload Status Upload In Progress"
        />
      );
    case "processing":
      return (
        <FormattedMessage
          defaultMessage="Processing"
          description="File Upload Status Processing"
        />
      );
  }
};
function InnerFileUploadArea(
  props: FileUploadAreaProps & {
    getUploadLink: (
      name: string,
      type: FileType
    ) => Promise<{ id: string; url: string }>;
    quoata?: {
      quota: number;
      used: number;
    };
  }
) {
  const insets = useSafeAreaInsets();
  const [fileStatus, setFileStatus] = useState<
    Record<
      string,
      | { status: "uploaded"; remoteId: string }
      | { status: "error"; error: ErrorReason }
    >
  >({});
  const [names, setNames] = useState<Record<string, string>>({});

  const filesToUpload = props.files.filter((f) => {
    return !fileStatus[f.id]?.status;
  });

  const uploadedFileIds = useMemo(
    () =>
      Object.entries(fileStatus).flatMap((e) =>
        e[1].status === "uploaded" ? [e[1].remoteId] : []
      ),
    [fileStatus]
  );

  useSubscription<FileUploadAreaSubscription>(
    useMemo(
      () => ({
        variables: {
          ids: uploadedFileIds,
        },
        subscription: graphql`
          subscription FileUploadAreaSubscription($ids: [ID!]!) {
            fileUpdate(ids: $ids) {
              id
              name
              status
            }
          }
        `,
      }),
      [uploadedFileIds]
    )
  );
  const [mode, setMode] = useState<"ADD_FILES" | "UPLOAD" | "DONE">(
    "ADD_FILES"
  );
  useEffect(() => {
    setMode("ADD_FILES");
  }, [props.files]);

  const uploadFiles = async () => {
    setMode("UPLOAD");
    try {
      await Promise.all(
        filesToUpload.map((pendingFile) => {
          const mimeType = pendingFile.type;
          const fileType = mimeToType(mimeType);
          if (!fileType) {
            setFileStatus((oldStatus) => ({
              ...oldStatus,
              [pendingFile.id]: { status: "error", error: "type" },
            }));
            return;
          }

          return new Promise<void>((resolve, reject) =>
            props
              .getUploadLink(
                names[pendingFile.id] ?? pendingFile.name,
                fileType
              )
              .then((uploadRespose) => {
                const headers = new Headers({
                  "Content-Type": mimeType,
                });

                if (typeof pendingFile.file === "string") {
                  FileSystem.uploadAsync(uploadRespose.url, pendingFile.file, {
                    httpMethod: "PUT",
                    mimeType,
                    headers: {
                      "Content-Type": mimeType,
                    },
                    sessionType: FileSystem.FileSystemSessionType.BACKGROUND,
                    uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
                  })
                    .then(() => {
                      setFileStatus((oldStatus) => ({
                        ...oldStatus,
                        [pendingFile.id]: {
                          status: "uploaded",
                          remoteId: uploadRespose.id,
                        },
                      }));

                      resolve();
                    })
                    .catch((e) => {
                      setFileStatus((oldStatus) => ({
                        ...oldStatus,
                        [pendingFile.id]: { status: "error", error: "unknown" },
                      }));

                      reject(e);
                    });
                } else {
                  fetch(uploadRespose.url, {
                    method: "PUT",
                    body: pendingFile.file,
                    headers,
                  })
                    .then((response) => {
                      if (!response.ok) {
                        throw new Error("Network response was not ok");
                      }
                      setFileStatus((oldStatus) => ({
                        ...oldStatus,
                        [pendingFile.id]: {
                          status: "uploaded",
                          remoteId: uploadRespose.id,
                        },
                      }));

                      resolve();
                    })
                    .catch((e) => {
                      setFileStatus((oldStatus) => ({
                        ...oldStatus,
                        [pendingFile.id]: { status: "error", error: "unknown" },
                      }));

                      reject(e);
                    });
                }
              })
          );
        })
      );
    } finally {
      setMode("DONE");
    }
  };

  const fileQuotaIsExceded =
    props.quoata && props.quoata.used >= props.quoata.quota;

  return (
    <Modal
      visible={props.files.length > 0}
      transparent={Platform.OS === "web"}
      presentationStyle={Platform.OS === "web" ? "overFullScreen" : "pageSheet"}
      animationType={Platform.OS === "web" ? "fade" : "slide"}
    >
      <DropFileZone
        type="FILE"
        maxSize={MAX_FILE_SIZE}
        onDropFiles={(files) => {
          props.onDropedFiles(
            files.map(({ file, type }) => ({
              file,
              type,
              id: cuid(),
              name: removeExtension(file.name),
              size: file.size,
            }))
          );
        }}
      >
        <Wrapper onRequestClose={props.onCancel}>
          {fileQuotaIsExceded ? null : (
            <Header
              title=""
              headerLeft={() => (
                <HeaderButton
                  nativeHeader={false}
                  disabled={mode === "UPLOAD"}
                  onPress={props.onCancel}
                  type="CLOSE"
                />
              )}
            />
          )}
          <Box color="background" style={{ flex: 1 }} space="medium">
            {props.quoata ? (
              <AHText spaceBottom="small" spaceHorizontal="medium">
                <FormattedMessage
                  defaultMessage="{used} of max {quota} files."
                  description="File Upload Area: File quota"
                  values={{
                    used: props.quoata.used,
                    quota: props.quoata.quota,
                  }}
                />
              </AHText>
            ) : null}
            {fileQuotaIsExceded ? (
              <FileQuoataExceded onDismiss={props.onCancel} />
            ) : (
              <>
                {props.files.length > 0 && (
                  <>
                    <ListItem isFirst>
                      <FileRowWrapper
                        icon={null}
                        title={
                          <AHText numberOfLines={1} type="subtitle">
                            <FormattedMessage
                              defaultMessage="Filename"
                              description="File Upload Area: Filename column title"
                            />
                          </AHText>
                        }
                        size={
                          <AHText
                            color="darkText"
                            numberOfLines={1}
                            type="subtitle"
                          >
                            <FormattedMessage
                              defaultMessage="Size"
                              description="File Upload Area: Size column title"
                            />
                          </AHText>
                        }
                        detail={
                          <AHText
                            color="darkText"
                            numberOfLines={1}
                            type="subtitle"
                          >
                            <FormattedMessage
                              defaultMessage="Status"
                              description="File Upload Area: Status column title"
                            />
                          </AHText>
                        }
                        action={null}
                      />
                    </ListItem>
                    <AHSeparator />
                  </>
                )}
                <Suspense
                  fallback={
                    <FileList
                      mode={mode}
                      fileStatus={fileStatus}
                      files={props.files}
                      names={names}
                      onRemoveFile={props.onRemoveFile}
                      remoteFiles={[]}
                      setName={(id, name) => {
                        setNames((oldNames) => ({
                          ...oldNames,
                          [id]: name,
                        }));
                      }}
                    />
                  }
                >
                  <RemoteFileList
                    mode={mode}
                    fileStatus={fileStatus}
                    names={names}
                    files={props.files}
                    onRemoveFile={props.onRemoveFile}
                    setName={(id, name) => {
                      setNames((oldNames) => ({
                        ...oldNames,
                        [id]: name,
                      }));
                    }}
                  />
                </Suspense>
              </>
            )}
          </Box>
          <Box
            insets={{ bottom: insets.bottom }}
            space="medium"
            color="surface"
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AHButton
              themeType="primary"
              title={mode === "DONE" ? "Klar" : "Ladda upp"}
              loading={mode === "UPLOAD"}
              onPress={() => {
                if (mode === "ADD_FILES") {
                  uploadFiles();
                } else {
                  props.onUploadedFiles?.(uploadedFileIds);
                  props.onCancel();
                }
              }}
              hideIfDisabled={mode === "DONE"}
              disabled={mode === "UPLOAD"}
            />
          </Box>
        </Wrapper>
      </DropFileZone>
    </Modal>
  );
}

type FileListProps = {
  mode: "ADD_FILES" | "UPLOAD" | "DONE";
  names: {
    [id: string]: string;
  };
  fileStatus: {
    [id: string]:
      | { status: "uploaded"; remoteId: string }
      | { status: "error"; error: ErrorReason };
  };
  files: PendingFile[];

  onRemoveFile(id: string): void;
  setName(id: string, name: string): void;
};
function RemoteFileList(props: FileListProps) {
  const uploadedFileIds = useMemo(
    () =>
      Object.entries(props.fileStatus).flatMap((e) =>
        e[1].status === "uploaded" ? [e[1].remoteId] : []
      ),
    [props.fileStatus]
  );

  const data = useLazyLoadQuery<FileUploadAreaStatusQuery>(
    graphql`
      query FileUploadAreaStatusQuery($fileIds: [ID!]!, $skip: Boolean!) {
        listFiles(fileIds: $fileIds) @skip(if: $skip) {
          id
          name
          status
        }
      }
    `,
    {
      fileIds: uploadedFileIds,
      skip: uploadedFileIds.length === 0,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );

  return <FileList {...props} remoteFiles={data.listFiles ?? []} />;
}
function FileList(
  props: {
    remoteFiles: readonly {
      readonly id: string;
      readonly status: FileStatus;
    }[];
  } & FileListProps
) {
  return (
    <FlatList
      data={props.files}
      ItemSeparatorComponent={AHSeparator}
      renderItem={({ item: file, index }) => {
        const name = props.names[file.id] ?? file.name;

        const currentFileStatus = props.fileStatus[file.id];
        const remoteId =
          currentFileStatus?.status === "uploaded"
            ? currentFileStatus?.remoteId
            : undefined;

        const status =
          (remoteId
            ? props?.remoteFiles?.find((l) => l.id === remoteId)?.status
            : undefined) ?? props.fileStatus[file.id]?.status;

        const parsedStatus = downlevelStatus(status);
        return (
          <UploadFileListItem
            name={name}
            size={formatBytes(file.size)}
            status={
              parsedStatus === "none" && props.mode === "UPLOAD"
                ? "uploading"
                : parsedStatus
            }
            isLast={index + 1 === props.files.length}
            setName={(name) => {
              props.setName(file.id, name);
            }}
            onRemove={() => {
              props.onRemoveFile(file.id);
            }}
          />
        );
      }}
    />
  );
}

function UploadFileListItem(props: {
  size: string;
  name: string;
  status: CanonicalStatus;
  isLast: boolean;
  onRemove(): void;
  setName(name: string): void;
}) {
  return (
    <ListItem disabled isLast={props.isLast}>
      <FileRowWrapper
        icon={
          <TouchableBox onPress={props.onRemove}>
            <StatusIndicator status={props.status} />
          </TouchableBox>
        }
        title={
          <AHInput
            textType="subtitle"
            cornerRadius="medium"
            color="background"
            value={props.name}
            editable={props.status === "none"}
            onChangeText={(v) => {
              props.setName(v);
            }}
            style={{
              maxWidth: "100%",
              paddingVertical: 10,
              paddingHorizontal: 10,
              marginRight: 20,
            }}
          />
        }
        size={
          <AHText
            color="darkText"
            numberOfLines={1}
            style={{ flex: 1 }}
            type="body"
          >
            {props.size}
          </AHText>
        }
        detail={
          <AHText
            color="darkText"
            numberOfLines={1}
            style={{ flex: 1 }}
            type="body"
          >
            <StatusMessage status={props.status} />
          </AHText>
        }
        action={null}
      />
    </ListItem>
  );
}
function StatusIndicator(props: { status: CanonicalStatus }) {
  switch (props.status) {
    case "processing":
    case "uploading":
      return (
        <Circle size="smallMedium" color="transparent">
          <ActivityIndicator />
        </Circle>
      );
    case "done":
      return (
        <Circle size="smallMedium" color="positive">
          <AHIcon type="check" size="small" color="lightText" />
        </Circle>
      );
    case "none":
    case "error":
      return (
        <Circle size="smallMedium" color="transparent">
          <AHIcon type="clear" forceSize={20} />
        </Circle>
      );
  }
  return null;
}

function Wrapper(props: {
  children: React.ReactNode;
  onRequestClose: () => void;
}) {
  const width = 800;
  const height = 700;

  if (Platform.OS !== "web") return <>{props.children}</>;

  return (
    <>
      <Pressable
        style={[StyleSheet.absoluteFill]}
        onPress={() => {
          props.onRequestClose();
        }}
      >
        <BlurView intensity={8} style={[StyleSheet.absoluteFill]} />
      </Pressable>
      <Box
        cornerRadius="large"
        color="surface"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width,
          height,
          marginLeft: -width / 2,
          marginTop: -height / 2,

          shadowColor: "#000",
          shadowOpacity: 0.2,
          shadowRadius: 10,
          overflow: "hidden",
        }}
      >
        {props.children}
      </Box>
    </>
  );
}
